/**
 * E07-F04-T09 — the ONE way a research long-list row gets a human label.
 *
 * The bug this module closes: a review/AI-check row that has already resolved to a
 * `companies` record loses its raw fields (`raw_name` / `raw_domain` go NULL — the
 * canonical data now lives on the relation), so any screen that printed the raw
 * fields fell through to a technical placeholder. On the AI-check screen that
 * placeholder was literally the row's primary key (`row 4711`), on the Review table
 * the website line read "no website" for a company whose domain we do know. The user
 * saw a database id instead of a company and could not tell why the row was there.
 *
 * INVARIANT: a display label is resolved ON THE SERVER, from the relation first, and
 * a row that genuinely has no name gets the EXPLICIT `UNRESOLVED_COMPANY_NAME` marker
 * — never a row id, never an empty cell. The UI must not reconstruct a name from the
 * raw columns; it renders `row.name` / `row.domain` and, when the name is the marker,
 * says so explicitly (`isUnresolvedCompanyName`).
 *
 * Pure + DOM-free: api projects rows with it, web recognises the marker with it.
 */
/**
 * The explicit "we could not determine this company's name" marker. Kept as the
 * literal BUG-5 wording so already-persisted payloads/tests keep matching, and
 * exported so nobody re-types the string (a typo would silently look like a name).
 */
export const UNRESOLVED_COMPANY_NAME = 'Unnamed company';
function firstNonEmpty(...values) {
    for (const value of values) {
        const trimmed = value?.trim();
        if (trimmed)
            return trimmed;
    }
    return null;
}
/**
 * Resolve the display name: canonicalName → rawName → rawDomain → explicit marker.
 * Never returns an empty string, so a caller can print it unconditionally.
 */
export function resolveCompanyDisplayName(source) {
    return (firstNonEmpty(source.canonicalName, source.rawName, source.rawDomain) ?? UNRESOLVED_COMPANY_NAME);
}
/**
 * Resolve the display website: the resolved company's domain first, the raw
 * discovery domain second, `null` when the row truly has no website on record.
 */
export function resolveCompanyDisplayDomain(source) {
    return firstNonEmpty(source.companyDomain, source.rawDomain);
}
/** True when the label is the marker, i.e. the row needs a human to name it. */
export function isUnresolvedCompanyName(name) {
    return !name?.trim() || name.trim() === UNRESOLVED_COMPANY_NAME;
}
//# sourceMappingURL=companyDisplay.js.map